import { RnnoiseWorkletNode, loadRnnoise } from '@sapphi-red/web-noise-suppressor';

// Assets are copied into public/ by scripts/copy-noise-assets.cjs.
const ASSET_BASE = `${process.env.PUBLIC_URL || ''}/voice-noise`;
const STORAGE_KEY = 'hope-voice-noise-filter';

let wasmPromise = null;
let sharedContext = null;
const contextsWithWorklet = new WeakSet();

export const noiseFilterSupported = typeof window !== 'undefined'
    && typeof window.AudioWorkletNode !== 'undefined'
    && typeof WebAssembly !== 'undefined';

export function readNoiseFilterPreference() {
    try { return window.localStorage.getItem(STORAGE_KEY) !== 'off'; } catch (_) { return true; }
}

export function saveNoiseFilterPreference(enabled) {
    try { window.localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off'); } catch (_) { /* private mode */ }
}

// RNNoise only works at 48 kHz, so the mic track gets its own context instead of the browser default rate.
export function noiseAudioContext() {
    if (!sharedContext || sharedContext.state === 'closed') {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        sharedContext = new AudioCtx({ sampleRate: 48000, latencyHint: 'interactive' });
    }
    return sharedContext;
}

function loadWasm() {
    if (!wasmPromise) {
        wasmPromise = loadRnnoise({ url: `${ASSET_BASE}/rnnoise.wasm`, simdUrl: `${ASSET_BASE}/rnnoise_simd.wasm` })
            .catch(error => { wasmPromise = null; throw error; });
    }
    return wasmPromise;
}

// LiveKit TrackProcessor: mic -> RNNoise worklet -> processed track that gets published.
export function createNoiseFilter() {
    let source = null;
    let node = null;

    const teardown = () => {
        source?.disconnect();
        node?.disconnect();
        node?.destroy();
        source = null;
        node = null;
    };

    const processor = {
        name: 'hope-rnnoise',
        processedTrack: undefined,
        async init({ track, audioContext }) {
            const wasmBinary = await loadWasm();
            if (!contextsWithWorklet.has(audioContext)) {
                await audioContext.audioWorklet.addModule(`${ASSET_BASE}/rnnoiseWorklet.js`);
                contextsWithWorklet.add(audioContext);
            }
            if (audioContext.state === 'suspended') await audioContext.resume().catch(() => {});
            source = audioContext.createMediaStreamSource(new MediaStream([track]));
            node = new RnnoiseWorkletNode(audioContext, { maxChannels: 1, wasmBinary });
            const destination = audioContext.createMediaStreamDestination();
            source.connect(node).connect(destination);
            processor.processedTrack = destination.stream.getAudioTracks()[0];
        },
        async restart(options) {
            teardown();
            await processor.init(options);
        },
        async destroy() {
            teardown();
            processor.processedTrack = undefined;
        }
    };
    return processor;
}
